/**
 * CooldownHookSystem — hook-based cooldown manipulation for passive nodes
 * and game events.
 *
 * Allows passive nodes to register cooldown modifications that fire on
 * specific triggers: kills, conditions, hit events, and status applications.
 * Supports percentage refunds, flat refunds, acceleration multipliers,
 * and flat base reductions.
 *
 * Enforces a global minimum cooldown floor of 100ms to prevent
 * zero-cooldown degeneracy from multi-tree stacking.
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CooldownTrigger =
  | { type: 'onKill'; skillId?: string; requireStealthKill?: boolean; stealthKill?: boolean; requireEnemyStatus?: string; enemyStatuses?: readonly string[] }
  | { type: 'onCondition'; conditionId: string }
  | { type: 'onHitLanded'; skillId: string }
  | { type: 'onStatusApplied'; statusId: string };

export type CooldownEffect =
  | { type: 'refundPercent'; percent: number }
  | { type: 'refundFlat'; ms: number }
  | { type: 'accelerate'; multiplier: number }
  | { type: 'reduceFlat'; ms: number };

export interface CooldownHook {
  /** Unique identifier for this hook (for unregistration). */
  readonly id: string;
  /** When this hook fires. */
  readonly trigger: CooldownTrigger;
  /** Which skill's cooldown to modify. */
  readonly targetSkillId: string;
  /** What modification to apply. */
  readonly effect: CooldownEffect;
}

/** Emitted when a refund hook fires, so the caller can apply the refund. */
export interface CooldownRefundEvent {
  readonly hookId: string;
  readonly targetSkillId: string;
  readonly refundMs: number;
}

/** Global minimum cooldown floor in ms — no skill can go below this. */
export const MINIMUM_COOLDOWN_MS = 100;

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class CooldownHookSystem {
  private readonly hooks = new Map<string, CooldownHook>();

  /**
   * Pending refunds from notify() calls, keyed by targetSkillId.
   * The caller (SkillActivationComponent) drains these each frame.
   */
  private readonly pendingRefunds = new Map<string, CooldownRefundEvent[]>();

  /** Register a cooldown hook (called when a passive node is unlocked). */
  register(hook: CooldownHook): void {
    this.hooks.set(hook.id, hook);
  }

  /** Unregister a hook (called on respec). */
  unregister(hookId: string): void {
    this.hooks.delete(hookId);
  }

  /**
   * Notify the system of a trigger event. Matches all registered hooks
   * against the trigger and queues refund events for matching refund hooks.
   *
   * For 'accelerate' and 'reduceFlat' hooks, the effect is persistent —
   * it's read via getAccelerationMultiplier / getFlatReduction. Only
   * 'refundPercent' and 'refundFlat' hooks produce events on notify.
   *
   * @param trigger The trigger event
   * @param baseCooldowns Map of skillId → base cooldown ms, needed to
   *   calculate percentage refunds. If not provided, percentage refunds
   *   are skipped.
   */
  notify(trigger: CooldownTrigger, baseCooldowns?: Map<string, number>): void {
    for (const hook of this.hooks.values()) {
      if (!this.triggerMatches(hook.trigger, trigger)) continue;

      if (hook.effect.type === 'refundPercent') {
        const baseCd = baseCooldowns?.get(hook.targetSkillId);
        if (baseCd === undefined) continue;
        const refundMs = Math.round(baseCd * hook.effect.percent);
        this.queueRefund(hook, refundMs);
      } else if (hook.effect.type === 'refundFlat') {
        this.queueRefund(hook, hook.effect.ms);
      }
      // accelerate and reduceFlat are persistent — no events needed
    }
  }

  /**
   * Drain pending refunds for a skill. Returns the queued refund events
   * and clears them. Call this each frame from the cooldown tick loop.
   */
  drainRefunds(skillId: string): CooldownRefundEvent[] {
    const refunds = this.pendingRefunds.get(skillId);
    if (!refunds || refunds.length === 0) return [];
    this.pendingRefunds.set(skillId, []);
    return refunds;
  }

  /**
   * Get the current cooldown acceleration multiplier for a skill.
   * Returns the product of all active 'accelerate' hooks targeting this skill.
   * A value > 1 means cooldowns tick faster (recover sooner).
   */
  getAccelerationMultiplier(skillId: string): number {
    let multiplier = 1;
    for (const hook of this.hooks.values()) {
      if (hook.targetSkillId !== skillId) continue;
      if (hook.effect.type !== 'accelerate') continue;
      multiplier *= hook.effect.multiplier;
    }
    return multiplier;
  }

  /**
   * Get the total flat reduction for a skill's base cooldown in ms.
   * The result is clamped so the effective cooldown cannot fall below
   * MINIMUM_COOLDOWN_MS.
   *
   * @param skillId The skill to query
   * @param baseCooldownMs The skill's unmodified base cooldown, used for clamping
   */
  getFlatReduction(skillId: string, baseCooldownMs: number): number {
    let totalReduction = 0;
    for (const hook of this.hooks.values()) {
      if (hook.targetSkillId !== skillId) continue;
      if (hook.effect.type !== 'reduceFlat') continue;
      totalReduction += hook.effect.ms;
    }
    // Clamp so effective cooldown never drops below the floor
    const maxReduction = Math.max(0, baseCooldownMs - MINIMUM_COOLDOWN_MS);
    return Math.min(totalReduction, maxReduction);
  }

  /**
   * Compute the effective cooldown for a skill after all persistent
   * modifications (flat reduction). Enforces the global minimum floor.
   */
  getEffectiveCooldown(skillId: string, baseCooldownMs: number): number {
    const reduction = this.getFlatReduction(skillId, baseCooldownMs);
    return Math.max(baseCooldownMs - reduction, MINIMUM_COOLDOWN_MS);
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  private triggerMatches(hookTrigger: CooldownTrigger, eventTrigger: CooldownTrigger): boolean {
    if (hookTrigger.type !== eventTrigger.type) return false;

    switch (hookTrigger.type) {
      case 'onKill': {
        const event = eventTrigger as { type: 'onKill'; skillId?: string; stealthKill?: boolean; enemyStatuses?: readonly string[] };
        // If hook specifies a skillId, it must match. Otherwise, any kill counts.
        if (hookTrigger.skillId !== undefined && hookTrigger.skillId !== event.skillId) return false;
        // If hook requires a stealth kill, the event must carry that flag.
        if (hookTrigger.requireStealthKill && !event.stealthKill) return false;
        // If hook requires the dying enemy to have a specific status, check it.
        if (hookTrigger.requireEnemyStatus !== undefined) {
          if (!event.enemyStatuses?.includes(hookTrigger.requireEnemyStatus)) return false;
        }
        return true;
      }
      case 'onCondition': {
        const event = eventTrigger as { type: 'onCondition'; conditionId: string };
        return hookTrigger.conditionId === event.conditionId;
      }
      case 'onHitLanded': {
        const event = eventTrigger as { type: 'onHitLanded'; skillId: string };
        return hookTrigger.skillId === event.skillId;
      }
      case 'onStatusApplied': {
        const event = eventTrigger as { type: 'onStatusApplied'; statusId: string };
        return hookTrigger.statusId === event.statusId;
      }
    }
  }

  private queueRefund(hook: CooldownHook, refundMs: number): void {
    const event: CooldownRefundEvent = {
      hookId: hook.id,
      targetSkillId: hook.targetSkillId,
      refundMs,
    };
    const existing = this.pendingRefunds.get(hook.targetSkillId);
    if (existing) {
      existing.push(event);
    } else {
      this.pendingRefunds.set(hook.targetSkillId, [event]);
    }
  }
}
